import t from "@/lang/lang";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { PaymentTermsCard } from "@/services/backend/billing/components/payment-terms-card";
import { BillingInformationCard } from "@/services/backend/billing/components/billing-information-card";
import {
  updateOmniContractCache,
  useOmniContractListQuery,
  PatchRequest,
  useOmniContractPatchMutation,
} from "@/services/backend/omni/contract/api/contract-api";
import {
  Contract,
  Patch,
  patchContract,
} from "@/services/backend/omni/contract/types/contract";
import { Offer } from "@/services/backend/omni/offer/types/offer";
import { ServiceVariation } from "@/services/backend/omni/servicevariation/types/service-variation";
import { ScopeOfWork } from "@/services/backend/omni/scope-of-work/types/scope-of-work";
import { CardSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { ContractStateField } from "@/services/backend/omni/contract/components/contract-state-field";
import { DatePickerField } from "@/shared/components/field/date-picker-field";
import { EditorInputField } from "@/shared/components/field/editor-input-field";

/**
 * OfferContractForServiceVariation edits the contract attached to the offer for
 * its scope of work of the service variation, see
 * `servicekindrules.WRHMilestoneOffer`,
 * backend/core/omni/domain/servicekindrules/wrh_offer_sync.go: exactly one
 * exists. Its scope of work is a copy of the offer's and is not edited here.
 *
 * The offered fields are limited to the ones strictly needed in WRH.
 * This can be extended if needed.
 */
export function OfferContractForServiceVariation({
  offer,
  serviceVariation,
}: {
  offer: Offer;
  serviceVariation: ServiceVariation;
}) {
  const { data, isLoading, error } = useOmniContractListQuery({
    offerId: { active: true, values: [offer.id] },
  });

  const contract = data?.items.find((c: Contract) =>
    c.scopeOfWork.some(
      (sow: ScopeOfWork) => sow.serviceVariation === serviceVariation,
    ),
  );

  return (
    <>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <CardSkeleton />}
      {contract && (
        <LimitedContractData
          contract={contract}
          serviceVariation={serviceVariation}
        />
      )}
    </>
  );
}

function LimitedContractData({
  contract,
  serviceVariation,
}: {
  contract: Contract;
  serviceVariation: ServiceVariation;
}) {
  const request: PatchRequest = { id: contract.id };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateOmniContractCache,
    useOmniContractPatchMutation(),
  );
  const patch = (p: Patch) =>
    set({ ...request, ...p }, patchContract(contract, p));
  const disabled = isImmutable(contract);

  return (
    <div className="grid gap-2 lg:grid-cols-2">
      <Card className="col-span-2">
        <CardHeader>
          <CardTitle>{contract.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 lg:grid-cols-3">
            <ContractStateField
              value={contract.contractState}
              onValueChange={(contractState) => patch({ contractState })}
              disabled={disabled}
            />
            <DatePickerField
              label={t("Vertragsbeginn")}
              nullable={true}
              date={contract.startDate}
              onDateChange={(date) => patch({ startDate: date ?? null })}
              fieldName="startDate"
              captionLayout="dropdown"
              disabled={disabled}
            />
            {serviceVariation === ServiceVariation.WRHFullService && (
              <DatePickerField
                label={t("Vertragsende")}
                nullable={true}
                date={contract.endDate}
                onDateChange={(date) => patch({ endDate: date ?? null })}
                fieldName="endDate"
                description={t(
                  "Ohne Vertragsende ist der Vertrag unbefristet.",
                )}
                captionLayout="dropdown"
                disabled={disabled}
              />
            )}
            <EditorInputField
              label={t("Besondere Vereinbarungen")}
              initialHtml={contract.specialAgreements}
              onHtmlChange={(specialAgreements) => patch({ specialAgreements })}
              fieldName="specialAgreements"
              fieldProps={{ className: "lg:col-span-3" }}
              disabled={disabled}
            />
          </div>
        </CardContent>
      </Card>
      <PaymentTermsCard
        paymentTerms={contract.paymentTerms}
        onPaymentTermsChange={(paymentTerms) => patch({ paymentTerms })}
        disabled={disabled}
      />
      <BillingInformationCard
        billingInformation={contract.billingInformation}
        onBillingInformationChange={(billingInformation) =>
          patch({ billingInformation })
        }
        disabled={disabled}
      />
    </div>
  );
}
